import { useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { isEqualTrackRef } from '@livekit/components-core'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { usePagination, useTracks, useVisualStableUpdate } from '@livekit/components-react'
import { Track } from 'livekit-client'

import { HiddenTracks } from 'modules/meetings/components/VideoGrid/components/HiddenTracks'
import { ParticipantTile } from 'modules/meetings/components/VideoGrid/components/ParticipantTile'
import { useMeetingSounds } from 'modules/meetings/lib/hooks/useMeetingSounds'
import { useMeetingSoundsSettings } from 'modules/meetings/lib/hooks/useMeetingSounds'
import { useVirtualBackground } from 'modules/meetings/lib/hooks/useVirtualBackground'
import { getTrackPositions } from 'modules/meetings/lib/utils/getTrackPositions'

const ITEM_PER_PAGE = {
  SMALL: 3,
  MEDIUM: 5,
  LARGE: 6,
}

export const VideoGrid = () => {
  const { enabled: soundsEnabled } = useMeetingSoundsSettings()
  const [isManualFocus, setIsManualFocus] = useState(false)
  const [isSmallMode, setIsSmallMode] = useState(false)
  const [isMediumMode, setIsMediumMode] = useState(false)

  const autoFocusedTracksRef = useRef<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  useMeetingSounds(soundsEnabled ?? false)
  useVirtualBackground()

  const itemPerPage = isSmallMode
    ? ITEM_PER_PAGE.SMALL
    : isMediumMode
      ? ITEM_PER_PAGE.MEDIUM
      : ITEM_PER_PAGE.LARGE

  const initialTracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  )

  const [focusedTrack, setFocusedTrack] = useState<TrackReferenceOrPlaceholder | undefined>(
    undefined
  )

  // speaking participants move to the first page and are always visible if there is overflow
  const sortedTracks = useVisualStableUpdate(
    initialTracks,
    focusedTrack ? itemPerPage - 1 : itemPerPage
  )
  // we delete the focused track from the list and add it to the first position
  const tracksWithFocus = useMemo(() => {
    if (!focusedTrack) return sortedTracks
    const rest = sortedTracks.filter(
      (t) =>
        !(t.participant.sid === focusedTrack.participant.sid && t.source === focusedTrack.source)
    )
    return [focusedTrack, ...rest]
  }, [sortedTracks, focusedTrack])

  const pagination = usePagination(itemPerPage, tracksWithFocus)
  const tracks = pagination.tracks

  useEffect(() => {
    if (!containerRef.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width
        setIsSmallMode(width < 768)
        setIsMediumMode(width < 1024)
      }
    })
    resizeObserver.observe(containerRef.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // Unified auto-focus logic for both screen shares and participants
  useEffect(() => {
    const screenShareTracks = sortedTracks.filter(
      (track) => track.source === Track.Source.ScreenShare
    )
    const cameraTracks = sortedTracks.filter((track) => track.source === Track.Source.Camera)

    let trackToFocus: TrackReferenceOrPlaceholder | undefined

    // Priority 1: Auto-focus new screen share tracks
    if (screenShareTracks.length > 0) {
      trackToFocus = screenShareTracks.find((track) => {
        const trackId = `${track.participant?.identity}-${track.source}`
        return !autoFocusedTracksRef.current.has(trackId)
      })
    }
    // Priority 2: Auto-focus remote participant when there are exactly 2 participants (local + 1 remote) and no screen sharing
    else {
      // Get unique participants from camera tracks
      const uniqueParticipants = Array.from(
        new Set(cameraTracks.map((track) => track.participant?.identity))
      ).filter(Boolean)

      // Check if we have exactly 2 participants (local + 1 remote)
      if (uniqueParticipants.length === 2) {
        const remoteParticipantTrack = cameraTracks.find((track) => !track.participant?.isLocal)

        if (remoteParticipantTrack) {
          const participantId = remoteParticipantTrack.participant?.identity

          // Auto-focus logic: only apply when user hasn't made manual focus decisions
          const isAlreadyFocusedOnRemote =
            focusedTrack &&
            focusedTrack.participant?.identity === participantId &&
            focusedTrack.source === Track.Source.Camera

          const hasScreenShareFocus =
            focusedTrack && focusedTrack.source === Track.Source.ScreenShare

          // Only auto-focus if no manual focus is active and conditions are met
          if (!isManualFocus && !isAlreadyFocusedOnRemote && !hasScreenShareFocus) {
            trackToFocus = remoteParticipantTrack
          }
        }
      }
    }

    // Apply the focus if a track was selected
    if (trackToFocus) {
      setFocusedTrack(trackToFocus)
      setIsManualFocus(false) // Reset manual flag when auto-focusing
      const trackId = `${trackToFocus.participant?.identity}-${trackToFocus.source}`
      autoFocusedTracksRef.current.add(trackId)
    }

    // Reset manual focus when participant count changes or focused track no longer exists
    if (isManualFocus && focusedTrack) {
      const focusedTrackExists = sortedTracks.some((track) => isEqualTrackRef(track, focusedTrack))
      if (!focusedTrackExists) {
        setIsManualFocus(false)
      }
    }

    // Clean up auto-focused tracking for tracks that no longer exist
    const currentTrackIds = new Set(
      sortedTracks.map((track) => `${track.participant?.identity}-${track.source}`)
    )

    const newAutoFocusedTracks = new Set<string>()
    autoFocusedTracksRef.current.forEach((id) => {
      if (currentTrackIds.has(id)) {
        newAutoFocusedTracks.add(id)
      }
    })
    autoFocusedTracksRef.current = newAutoFocusedTracks
  }, [sortedTracks, focusedTrack, tracks, tracksWithFocus, isManualFocus])

  useEffect(() => {
    if (!focusedTrack) return

    const trackStillExists = sortedTracks.some((track) => isEqualTrackRef(track, focusedTrack))
    const lessThan2Tracks = sortedTracks.length < 2
    if (!trackStillExists || lessThan2Tracks) {
      setFocusedTrack(undefined)
    }
  }, [sortedTracks, focusedTrack])

  const handleTrackFocus = (track: TrackReferenceOrPlaceholder) => {
    setFocusedTrack(track)
    setIsManualFocus(true)
  }

  const handleTrackUnfocus = () => {
    setFocusedTrack(undefined)
    setIsManualFocus(true)
  }

  const trakcsToRender = tracks.map((track, index) => {
    const participant = track.participant
    if (!participant) return null
    return (
      <ParticipantTile
        key={`${track.participant?.identity || 'local'}-${track.source || 'unknown'}-${index}`}
        trackRef={track}
        participant={participant}
        numberOfTracks={tracks.length}
        onFocus={() => handleTrackFocus(track)}
        onUnfocus={handleTrackUnfocus}
        isFocused={focusedTrack ? isEqualTrackRef(track, focusedTrack) : false}
      />
    )
  })

  const focusedTrackIndex = focusedTrack
    ? tracks.findIndex((track) => isEqualTrackRef(track, focusedTrack))
    : undefined

  const amountOfPositionsToRender =
    initialTracks.length > itemPerPage ? itemPerPage + 1 : tracks.length

  const positions = getTrackPositions(
    amountOfPositionsToRender,
    isSmallMode,
    isMediumMode,
    focusedTrackIndex
  )

  return (
    <Box
      ref={containerRef}
      position="relative"
      width="full"
      style={{ maxWidth: '1536px' }}
      height="full"
    >
      {positions.map((position, index) => (
        <Box
          key={index}
          position="absolute"
          style={{
            ...position,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {index !== itemPerPage ? (
            trakcsToRender[index]
          ) : (
            <HiddenTracks
              isSmallMode={isSmallMode}
              initialTracks={initialTracks}
              tracks={tracks}
              isFocusActive={focusedTrack ? true : false}
            />
          )}
        </Box>
      ))}
    </Box>
  )
}
